import { useEffect, useState } from 'react';
import { recallsApi, suppliersApi, tenantsApi } from '../api/client';
import type { RecallScope, RecallEvent, RecallAffectedProduct } from '../api/client';
import { useApi } from '../hooks/useApi';
import { T, SectionHeader, Card, Chip, ErrorBox, Skeleton } from '../components/ui';

// ── Recall reviewer email panel (v7.14.0 / Sequencing #9, Bundle B) ──────────
// Inline panel — single text input + Save/Clear — bound to the new
// PATCH /api/tenants/me/recall-reviewer-email endpoint. Lives on RecallPage
// because the column is operational recall config; the existing /api/tenants/me
// already exposes the current value as part of t.*.
function RecallReviewerEmailPanel() {
  const [value,   setValue]   = useState<string>('');
  const [saved,   setSaved]   = useState<string | null>(null);  // last persisted value
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState<string>('');
  const [err,     setErr]     = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    tenantsApi.me()
      .then(t => { if (!cancelled) { setValue(t.recall_reviewer_email ?? ''); setSaved(t.recall_reviewer_email); } })
      .catch(e => { if (!cancelled) setErr(e instanceof Error ? e.message : 'Failed to load tenant'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const dirty = (value.trim() || null) !== (saved ?? null);

  const save = async (next: string | null) => {
    setSaving(true); setMsg(''); setErr('');
    try {
      const updated = await tenantsApi.updateRecallReviewerEmail(next);
      setSaved(updated.recall_reviewer_email);
      setValue(updated.recall_reviewer_email ?? '');
      setMsg(next === null ? 'Reviewer email cleared.' : 'Reviewer email saved.');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = { display: 'block', color: T.sub, fontSize: 10, textTransform: 'uppercase' as const, letterSpacing: .8, fontWeight: 600, marginBottom: 6 };
  const btnStyle = (kind: 'primary' | 'ghost', disabled: boolean) => ({
    padding: '8px 14px', borderRadius: 8, fontSize: 11, fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    background: disabled ? T.border : kind === 'primary' ? T.sapphire : T.panel,
    color: disabled ? T.sub : kind === 'primary' ? '#fff' : T.sub,
    border: `1px solid ${kind === 'primary' ? T.sapphire : T.border}`,
  });

  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <label style={labelStyle} htmlFor="recall-reviewer-email">Recall reviewer email</label>
          {loading
            ? <Skeleton h={36} />
            : <input
                id="recall-reviewer-email"
                type="email"
                value={value}
                placeholder="ops@example.com (empty to disable email leg)"
                onChange={e => setValue(e.target.value)}
                disabled={saving}
                style={{
                  width: '100%', background: T.card, border: `1px solid ${T.border}`,
                  borderRadius: 8, padding: '9px 12px', color: T.text, fontSize: 12,
                  outline: 'none', fontFamily: "'DM Sans', sans-serif",
                }}
              />
          }
          <div style={{ marginTop: 6, fontSize: 10, color: T.sub, lineHeight: 1.5 }}>
            When set, the recall workflow's <code>subscriber_notify_email</code> leg sends the
            <code> recall_notification</code> template to this address on every recall. Leave empty
            to keep the leg fail-soft (logs <code>recall_stepper.email_skipped</code>, no message sent).
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <button
            onClick={() => save(value.trim() || null)}
            disabled={saving || loading || !dirty}
            style={btnStyle('primary', saving || loading || !dirty)}
          >{saving ? 'Saving…' : 'Save'}</button>
          <button
            onClick={() => save(null)}
            disabled={saving || loading || saved === null}
            style={btnStyle('ghost', saving || loading || saved === null)}
          >Clear</button>
        </div>
      </div>
      {msg && <div style={{ marginTop: 10, color: T.sage, fontSize: 11 }}>{msg}</div>}
      {err && <div style={{ marginTop: 10 }}><ErrorBox message={err} /></div>}
    </Card>
  );
}

// ── Colours ───────────────────────────────────────────────────────
const SEV: Record<string, string> = {
  simulating: T.amber, active: T.crimson, monitoring: '#e87a3a', closed: T.sage,
};
const HAZARD_ICONS: Record<string, string> = {
  contamination: '☣', mislabeling: '⚠', allergen: '🥜', pathogen: '🦠', default: '⚠',
};

// ── Sub-components ────────────────────────────────────────────────
function AffectedProductRow({
  item, onUpdateStatus,
}: { key?: React.Key; item: RecallAffectedProduct; onUpdateStatus: (id: string, status: string) => void | Promise<void> }) {
  const STATUS_C: Record<string, string> = {
    pending: T.amber, notified: T.sapphire, removed: T.sage, confirmed: T.sage,
  };
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '10px 14px', borderBottom: `1px solid ${T.border}`,
    }}>
      <span style={{ fontSize: 20, flexShrink: 0 }}>{item.img_emoji || '📦'}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{item.product_name}</div>
        <div style={{ fontSize: 10, color: T.sub, display: 'flex', gap: 12, marginTop: 2 }}>
          {item.retailer_name && <span>🏪 {item.retailer_name}</span>}
          {item.lot_number    && <span style={{ fontFamily: "'DM Mono', monospace" }}>📋 {item.lot_number}</span>}
          {item.quantity      && <span>🔢 {item.quantity.toLocaleString()} units</span>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Chip label={item.status} color={STATUS_C[item.status] || T.sub} />
        {item.status === 'pending' && (
          <button onClick={() => onUpdateStatus(item.id, 'notified')} style={{
            background: `${T.sapphire}15`, border: `1px solid ${T.sapphire}35`,
            color: T.sapphire, borderRadius: 7, padding: '4px 10px', cursor: 'pointer', fontSize: 10, fontWeight: 600,
          }}>Mark Notified</button>
        )}
        {item.status === 'notified' && (
          <button onClick={() => onUpdateStatus(item.id, 'removed')} style={{
            background: `${T.sage}15`, border: `1px solid ${T.sage}35`,
            color: T.sage, borderRadius: 7, padding: '4px 10px', cursor: 'pointer', fontSize: 10, fontWeight: 600,
          }}>Mark Removed</button>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────
export default function RecallPage() {
  const [activeTab, setActiveTab]     = useState<'initiate' | 'active' | 'history'>('active');
  const [selectedRecall, setSelected] = useState<(RecallEvent & { affected_products: RecallAffectedProduct[] }) | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Initiate form state
  const [form, setForm] = useState({
    supplierId: '', productId: '', lotNumber: '',
    hazardType: 'contamination', pathogen: '', description: '',
    recallType: 'voluntary' as 'voluntary' | 'mandatory' | 'market_withdrawal',
  });
  const [initiating,  setInitiating]  = useState(false);
  const [traceResult, setTraceResult] = useState<RecallScope | null>(null);
  const [traceStep,   setTraceStep]   = useState(0);
  const [traceError,  setTraceError]  = useState('');

  // Data
  const { data: recallsData, loading: recallsLoading, refetch: refetchRecalls } = useApi(
    () => recallsApi.list(), []
  );
  const { data: suppliersData } = useApi(() => suppliersApi.list(), []);
  const recallList = recallsData?.recalls || [];
  const supplierList = suppliersData || [];

  const TRACE_STEPS = [
    'Querying product_suppliers graph…',
    'Traversing affected products…',
    'Loading lot-level records…',
    'Joining retailer data…',
    'Computing recall scope…',
    'Generating AI scope summary…',
    'Writing recall_affected_products…',
    'Firing critical alert…',
    'Trace complete ✓',
  ];

  const handleInitiate = async () => {
    if (!form.description || !form.hazardType) return;
    if (!form.supplierId && !form.productId && !form.lotNumber) {
      setTraceError('Provide at least one of: Supplier, Product ID, or Lot Number');
      return;
    }
    setInitiating(true); setTraceResult(null); setTraceStep(0); setTraceError('');

    // Animate trace steps
    const stepTimer = setInterval(() => {
      setTraceStep(s => Math.min(s + 1, TRACE_STEPS.length - 1));
    }, 180);

    try {
      const result = await recallsApi.initiate({
        supplierId:  form.supplierId  || undefined,
        productId:   form.productId   || undefined,
        lotNumber:   form.lotNumber   || undefined,
        hazardType:  form.hazardType,
        pathogen:    form.pathogen    || undefined,
        description: form.description,
        recallType:  form.recallType,
      });
      clearInterval(stepTimer);
      setTraceStep(TRACE_STEPS.length);
      setTraceResult(result);
      refetchRecalls();
      setActiveTab('active');
    } catch (err) {
      clearInterval(stepTimer);
      setTraceError(err instanceof Error ? err.message : 'Recall initiation failed');
    } finally {
      setInitiating(false);
    }
  };

  const loadDetail = async (id: string) => {
    setLoadingDetail(true);
    try {
      const detail = await recallsApi.get(id);
      setSelected(detail);
    } catch { /* silent */ }
    setLoadingDetail(false);
  };

  const updateProductStatus = async (affectedId: string, status: string) => {
    if (!selectedRecall) return;
    // Find the product from affected list using affectedId (it's the rap.id)
    const item = selectedRecall.affected_products.find(p => p.id === affectedId);
    if (!item) return;
    await recallsApi.updateProduct(selectedRecall.id, item.product_id, { status });
    await loadDetail(selectedRecall.id);
  };

  const closeRecall = async (id: string) => {
    if (!window.confirm('Close this recall? This cannot be undone.')) return;
    await recallsApi.close(id);
    setSelected(null);
    refetchRecalls();
  };

  const input = (label: string, key: keyof typeof form, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', color: T.sub, fontSize: 10, textTransform: 'uppercase', letterSpacing: .8, fontWeight: 600, marginBottom: 6 }}>{label}</label>
      <input
        value={String(form[key])}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        style={{ width: '100%', background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: '9px 12px', color: T.text, fontSize: 12, outline: 'none', fontFamily: "'DM Sans', sans-serif" }}
        {...props}
      />
    </div>
  );

  const select = (label: string, key: keyof typeof form, options: string[]) => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', color: T.sub, fontSize: 10, textTransform: 'uppercase', letterSpacing: .8, fontWeight: 600, marginBottom: 6 }}>{label}</label>
      <select
        value={String(form[key])}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        style={{ width: '100%', background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: '9px 12px', color: T.text, fontSize: 12, outline: 'none', cursor: 'pointer' }}
      >
        {options.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
      </select>
    </div>
  );

  return (
    <div className="fade-up">
      <SectionHeader
        title="Recall Engine"
        sub="FDA FSMA 204 compliant — trace contamination source to every affected product, lot, and retailer"
        right={
          <div style={{ display: 'flex', gap: 8 }}>
            <Chip label="FSMA 204" color={T.sapphire} />
            <Chip label={`${recallList.filter(r => r.status === 'active').length} Active`} color={recallList.some(r => r.status === 'active') ? T.crimson : T.sage} dot />
          </div>
        }
      />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {(['initiate', 'active', 'history'] as const).map(t => (
          <button key={t} onClick={() => setActiveTab(t)} style={{
            background: activeTab === t ? `${T.crimson}15` : T.panel,
            border: `1px solid ${activeTab === t ? T.crimson + '40' : T.border}`,
            color: activeTab === t ? T.crimson : T.sub,
            borderRadius: 8, padding: '8px 18px', cursor: 'pointer',
            fontSize: 12, fontWeight: activeTab === t ? 700 : 400,
          }}>
            {t === 'initiate' ? '+ New Recall' : t === 'active' ? `Active (${recallList.filter(r => r.status === 'active' || r.status === 'simulating').length})` : 'History'}
          </button>
        ))}
      </div>

      {/* Recall reviewer email — tenant config for the email-leg recipient (v7.14.0 Bundle B) */}
      <RecallReviewerEmailPanel />

      {/* ── INITIATE TAB ── */}
      {activeTab === 'initiate' && (
        <div style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: 20 }}>
          <Card>
            <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 16, color: T.text, marginBottom: 16 }}>Contamination Source</div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', color: T.sub, fontSize: 10, textTransform: 'uppercase', letterSpacing: .8, fontWeight: 600, marginBottom: 6 }}>Supplier</label>
              <select
                value={form.supplierId}
                onChange={e => setForm(f => ({ ...f, supplierId: e.target.value }))}
                style={{ width: '100%', background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: '9px 12px', color: form.supplierId ? T.text : T.sub, fontSize: 12, outline: 'none', cursor: 'pointer' }}
              >
                <option value="">— Select supplier —</option>
                {supplierList.map((s: any) => <option key={s.id} value={s.id}>{s.name} ({s.country})</option>)}
              </select>
            </div>

            {input('Product ID (optional)', 'productId', { placeholder: 'UUID — narrows scope to single product' })}
            {input('Lot Number (optional)', 'lotNumber', { placeholder: 'e.g. LOT-2026-EVOO-001' })}

            <div style={{ height: 1, background: T.border, margin: '16px 0' }} />

            {select('Hazard Type', 'hazardType', ['contamination', 'mislabeling', 'allergen', 'pathogen', 'foreign_object'])}
            {input('Pathogen (if applicable)', 'pathogen', { placeholder: 'e.g. Listeria monocytogenes, E. coli O157:H7' })}
            {select('Recall Type', 'recallType', ['voluntary', 'mandatory', 'market_withdrawal'])}

            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', color: T.sub, fontSize: 10, textTransform: 'uppercase', letterSpacing: .8, fontWeight: 600, marginBottom: 6 }}>Description *</label>
              <textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={3}
                placeholder="Describe the contamination event, detection method, and any known affected lots…"
                style={{ width: '100%', background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: '9px 12px', color: T.text, fontSize: 12, outline: 'none', resize: 'vertical', fontFamily: "'DM Sans', sans-serif" }}
              />
            </div>

            {traceError && <ErrorBox message={traceError} />}

            <button onClick={handleInitiate} disabled={initiating || !form.description} style={{
              width: '100%', padding: '12px',
              background: initiating || !form.description ? T.border : `linear-gradient(135deg, ${T.crimson}, #e07848)`,
              border: 'none', borderRadius: 10,
              color: initiating || !form.description ? T.sub : '#fff',
              fontWeight: 700, fontSize: 13, cursor: initiating || !form.description ? 'not-allowed' : 'pointer',
            }}>
              {initiating ? 'Tracing…' : '⚠ Initiate Recall Trace'}
            </button>
          </Card>

          {/* Trace log */}
          <Card>
            <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 16, color: T.text, marginBottom: 16 }}>
              Recall Trace Log
            </div>
            {!initiating && !traceResult && traceStep === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 24px', color: T.sub, fontSize: 12 }}>
                Configure the contamination source and click Initiate. The engine will traverse the full supply graph in milliseconds.
              </div>
            ) : (
              <>
                <div style={{ background: '#050405', borderRadius: 8, padding: '14px 16px', fontFamily: "'DM Mono', monospace", fontSize: 11, marginBottom: 16 }}>
                  {TRACE_STEPS.slice(0, traceStep).map((step, i) => (
                    <div key={i} style={{ color: i < traceStep - 1 ? T.sage : T.amber, marginBottom: 5, display: 'flex', gap: 10 }}>
                      <span>{i < traceStep - 1 ? '✓' : '→'}</span>
                      <span>{step}</span>
                    </div>
                  ))}
                  {initiating && (
                    <div style={{ display: 'flex', gap: 5, marginTop: 6 }}>
                      {[0,1,2].map(j => <div key={j} style={{ width: 5, height: 5, borderRadius: '50%', background: T.amber, animation: `pulse 1s infinite ${j * .2}s` }} />)}
                    </div>
                  )}
                </div>

                {traceResult && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
                      {[
                        ['Products Affected', traceResult.affectedProducts.length, T.crimson],
                        ['Retailers',         traceResult.affectedRetailers.length, '#e87a3a'],
                        ['Total Units',       traceResult.totalUnits.toLocaleString(), T.amber],
                      ].map(([l, v, c]) => (
                        <div key={String(l)} style={{ background: `${c}10`, border: `1px solid ${c}30`, borderRadius: 9, padding: '12px', textAlign: 'center' }}>
                          <div style={{ fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 22, color: String(c) }}>{v}</div>
                          <div style={{ fontSize: 9, color: T.sub, textTransform: 'uppercase', letterSpacing: .6, marginTop: 3 }}>{l}</div>
                        </div>
                      ))}
                    </div>

                    <div style={{ background: `${T.crimson}08`, border: `1px solid ${T.crimson}20`, borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
                      <div style={{ fontSize: 9, color: T.crimson, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 6 }}>AI Scope Summary</div>
                      <div style={{ fontSize: 12, color: T.sub, lineHeight: 1.6 }}>{traceResult.scopeSummary}</div>
                    </div>

                    <div style={{ fontSize: 10, color: T.dim, textAlign: 'right' }}>
                      Trace completed in {traceResult.traceTimeMs}ms — Recall ID: <span style={{ fontFamily: "'DM Mono', monospace", color: T.sub }}>{traceResult.recallId.slice(0, 16)}…</span>
                    </div>
                  </>
                )}
              </>
            )}
          </Card>
        </div>
      )}

      {/* ── ACTIVE / HISTORY TAB ── */}
      {(activeTab === 'active' || activeTab === 'history') && (
        <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 20 }}>
          {/* List */}
          <div>
            {recallsLoading
              ? [1,2,3].map(i => <div key={i} style={{ marginBottom: 10 }}><Skeleton h={80} /></div>)
              : recallList
                  .filter(r => activeTab === 'active'
                    ? r.status === 'active' || r.status === 'simulating' || r.status === 'monitoring'
                    : r.status === 'closed'
                  )
                  .map(r => (
                <div key={r.id} onClick={() => loadDetail(r.id)} style={{
                  background: selectedRecall?.id === r.id ? T.lift : T.panel,
                  border: `1px solid ${selectedRecall?.id === r.id ? T.crimson + '50' : T.border}`,
                  borderLeft: `3px solid ${SEV[r.status] || T.sub}`,
                  borderRadius: 10, padding: '13px 15px', marginBottom: 8,
                  cursor: 'pointer', transition: 'all .12s',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: T.text, flex: 1, marginRight: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</span>
                    <Chip label={r.status.toUpperCase()} color={SEV[r.status] || T.sub} />
                  </div>
                  <div style={{ display: 'flex', gap: 12, fontSize: 10, color: T.sub }}>
                    <span>{HAZARD_ICONS[r.hazard_type] || HAZARD_ICONS.default} {r.hazard_type}</span>
                    {r.affected_units != null && <span>📦 {r.affected_units.toLocaleString()} units</span>}
                    <span>{new Date(r.initiated_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))
            }
            {!recallsLoading && recallList.filter(r => activeTab === 'active' ? r.status !== 'closed' : r.status === 'closed').length === 0 && (
              <div style={{ color: T.sub, fontSize: 12, textAlign: 'center', padding: '40px 0' }}>
                {activeTab === 'active' ? 'No active recalls' : 'No closed recalls'}
              </div>
            )}
          </div>

          {/* Detail */}
          <div>
            {loadingDetail && <Skeleton h={400} />}
            {!loadingDetail && !selectedRecall && (
              <Card style={{ textAlign: 'center', padding: '48px' }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>⚠</div>
                <div style={{ color: T.sub, fontSize: 12 }}>Select a recall to see scope, affected products, and status management</div>
              </Card>
            )}
            {!loadingDetail && selectedRecall && (
              <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                  <div>
                    <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 17, color: T.text, marginBottom: 6 }}>{selectedRecall.title}</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Chip label={selectedRecall.status.toUpperCase()} color={SEV[selectedRecall.status] || T.sub} />
                      <Chip label={selectedRecall.recall_type} color={T.sub} />
                      <Chip label={selectedRecall.hazard_type} color={T.amber} />
                    </div>
                  </div>
                  {selectedRecall.status !== 'closed' && (
                    <button onClick={() => closeRecall(selectedRecall.id)} style={{
                      background: `${T.sage}15`, border: `1px solid ${T.sage}30`,
                      color: T.sage, borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 11, fontWeight: 600,
                    }}>Close Recall</button>
                  )}
                </div>

                {selectedRecall.scope_summary && (
                  <div style={{ background: `${T.crimson}08`, border: `1px solid ${T.crimson}20`, borderRadius: 8, padding: '12px 14px', marginBottom: 16, fontSize: 12, color: T.sub, lineHeight: 1.6 }}>
                    {selectedRecall.scope_summary}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
                  {[
                    ['Affected Products', selectedRecall.affected_products.length, T.crimson],
                    ['Retailers', selectedRecall.affected_retailers?.length || 0, '#e87a3a'],
                    ['Total Units', (selectedRecall.affected_units || 0).toLocaleString(), T.amber],
                  ].map(([l, v, c]) => (
                    <div key={String(l)} style={{ background: T.card, borderRadius: 8, padding: '10px', textAlign: 'center', border: `1px solid ${T.border}` }}>
                      <div style={{ fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 18, color: String(c) }}>{v}</div>
                      <div style={{ fontSize: 9, color: T.sub, textTransform: 'uppercase', letterSpacing: .5, marginTop: 2 }}>{l}</div>
                    </div>
                  ))}
                </div>

                <div style={{ fontSize: 10, color: T.sub, textTransform: 'uppercase', letterSpacing: .8, marginBottom: 10 }}>
                  Affected Products & Lots
                </div>
                {selectedRecall.affected_products.length === 0
                  ? <div style={{ color: T.sub, fontSize: 12, textAlign: 'center', padding: '20px 0' }}>No affected products recorded</div>
                  : selectedRecall.affected_products.map(item => (
                    <AffectedProductRow key={item.id} item={item} onUpdateStatus={(id, s) => { updateProductStatus(id, s); }} />
                  ))
                }
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
